import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FaBold,
  FaItalic,
  FaLink,
  FaUnderline,
  FaEraser,
} from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useEditMode } from '../../context/EditModeContext';
import { applyRichTextCommand, RICH_TEXT_COLORS } from '../../utils/richText';
import './editable.css';

function getToolbarPosition(element) {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  const toolbarWidth = 320;
  const left = Math.min(
    Math.max(12, rect.left + rect.width / 2 - toolbarWidth / 2),
    window.innerWidth - toolbarWidth - 12
  );
  const top = Math.max(12, rect.top - 52);
  return { left, top, width: toolbarWidth };
}

export default function RichTextToolbar() {
  const { isEditing } = useAuth();
  const { richTextElement } = useEditMode();
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!richTextElement) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      setPosition(getToolbarPosition(richTextElement));
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [richTextElement]);

  if (!isEditing || !richTextElement || !position) return null;

  const keepFocus = (event) => {
    event.preventDefault();
  };

  const runCommand = (command, value = null) => {
    richTextElement?.focus();
    applyRichTextCommand(command, value);
  };

  const handleLink = () => {
    const url = window.prompt('Link URL (https://...)');
    if (!url?.trim()) return;
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed)) {
      window.alert('Please enter a valid http or https URL.');
      return;
    }
    runCommand('createLink', trimmed);
  };

  return createPortal(
    <div
      className="rich-text-toolbar"
      style={{ left: position.left, top: position.top, width: position.width }}
      role="toolbar"
      aria-label="Text formatting"
      onMouseDown={keepFocus}
    >
      <button type="button" className="rich-text-toolbar__btn" title="Bold" onClick={() => runCommand('bold')}>
        <FaBold aria-hidden="true" />
      </button>
      <button type="button" className="rich-text-toolbar__btn" title="Italic" onClick={() => runCommand('italic')}>
        <FaItalic aria-hidden="true" />
      </button>
      <button type="button" className="rich-text-toolbar__btn" title="Underline" onClick={() => runCommand('underline')}>
        <FaUnderline aria-hidden="true" />
      </button>
      <span className="rich-text-toolbar__divider" aria-hidden="true" />
      <label className="rich-text-toolbar__color-label">
        <span className="sr-only">Text color</span>
        <select
          className="rich-text-toolbar__color-select"
          defaultValue="inherit"
          onChange={(e) => runCommand('foreColor', e.target.value === 'inherit' ? '#111111' : e.target.value)}
        >
          {RICH_TEXT_COLORS.map((color) => (
            <option key={color.label} value={color.value}>
              {color.label}
            </option>
          ))}
        </select>
      </label>
      <button type="button" className="rich-text-toolbar__btn" title="Add link" onClick={handleLink}>
        <FaLink aria-hidden="true" />
      </button>
      <button type="button" className="rich-text-toolbar__btn" title="Clear formatting" onClick={() => runCommand('removeFormat')}>
        <FaEraser aria-hidden="true" />
      </button>
    </div>,
    document.body
  );
}
